<template>
  <section class="account-manager" aria-label="资金账户管理">
    <div class="section-heading">
      <div>
        <h3>资金账户</h3>
        <p>正式维护收款使用的银行及现金账户；停用后新建收款单不再可选，历史单据保留账户快照。</p>
      </div>
    </div>

    <div class="filter-bar">
      <label><span>关键字</span><a-input v-model.trim="keyword" allow-clear placeholder="账户名称、开户行、户名或账号" @pressEnter="load" /></label>
      <label><span>经营主体 ID</span><a-input-number v-model="subjectId" :min="0" :precision="0" style="width:100%" placeholder="全部" /></label>
      <div class="filter-actions"><a-button type="primary" icon="search" @click="load">查询</a-button><a-button icon="redo" @click="reset">重置</a-button></div>
    </div>

    <div class="table-toolbar">
      <a-button type="primary" icon="plus" @click="openCreate">新增资金账户</a-button>
      <span>资金账户必须与收款单经营主体一致。</span>
    </div>

    <a-table
      row-key="id"
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="false"
      :scroll="{ x: 1050 }"
      bordered
      size="middle">
      <template slot="accountNo" slot-scope="value"><span class="mono">{{ value || '—' }}</span></template>
      <template slot="subject" slot-scope="value"><span class="subject-pill">主体 {{ value }}</span></template>
      <template slot="status" slot-scope="value, record">
        <a-switch :checked="value === 'enabled'" :loading="statusId === record.id" @change="checked => changeStatus(record, checked)" />
        <span class="status-text">{{ value === 'enabled' ? '启用' : '停用' }}</span>
      </template>
      <template slot="actions" slot-scope="value, record"><a @click="openEdit(record)">编辑</a></template>
    </a-table>

    <a-modal
      :visible="visible"
      :title="form.id ? '编辑资金账户' : '新增资金账户'"
      :confirm-loading="saving"
      width="680px"
      ok-text="保存"
      @ok="submit"
      @cancel="visible=false">
      <a-form-model ref="form" :model="form" :rules="rules" layout="vertical">
        <div class="form-grid">
          <a-form-model-item label="账户名称" prop="accountName"><a-input v-model.trim="form.accountName" :max-length="128" placeholder="如：工商银行基本户" /></a-form-model-item>
          <a-form-model-item label="经营主体 ID" prop="operatingSubjectId"><a-input-number v-model="form.operatingSubjectId" :disabled="!!form.id" :min="0" :precision="0" style="width:100%" /></a-form-model-item>
          <a-form-model-item label="开户行" prop="bankName"><a-input v-model.trim="form.bankName" :max-length="255" placeholder="开户银行及支行" /></a-form-model-item>
          <a-form-model-item label="户名" prop="accountHolder"><a-input v-model.trim="form.accountHolder" :max-length="255" placeholder="账户户名" /></a-form-model-item>
          <a-form-model-item class="full" label="账号" prop="accountNo"><a-input v-model.trim="form.accountNo" :max-length="128" placeholder="银行账号或现金账户标识" /></a-form-model-item>
          <a-form-model-item class="full" label="备注"><a-textarea v-model="form.remark" :rows="3" :max-length="500" placeholder="选填" /></a-form-model-item>
        </div>
      </a-form-model>
    </a-modal>
  </section>
</template>

<script>
import { listFinanceCashAccounts, saveFinanceCashAccount, updateFinanceCashAccountStatus } from '@/api/erp/finance'

export default {
  name: 'CashAccountTable',
  data () {
    return {
      keyword: '',
subjectId: undefined,
rows: [],
loading: false,
saving: false,
visible: false,
statusId: null,
      form: this.emptyForm(),
      rules: {
        accountName: [{ required: true, whitespace: true, message: '请输入账户名称', trigger: 'blur' }],
        operatingSubjectId: [{ required: true, message: '请输入经营主体', trigger: 'change' }]
      },
      columns: [
        { title: '账户编码', dataIndex: 'accountCode', width: 175 },
        { title: '账户名称', dataIndex: 'accountName', width: 170 },
        { title: '开户行', dataIndex: 'bankName', width: 180, customRender: value => value || '—' },
        { title: '户名', dataIndex: 'accountHolder', width: 150, customRender: value => value || '—' },
        { title: '账号', dataIndex: 'accountNo', width: 180, scopedSlots: { customRender: 'accountNo' } },
        { title: '经营主体', dataIndex: 'operatingSubjectId', width: 120, scopedSlots: { customRender: 'subject' } },
        { title: '状态', dataIndex: 'status', width: 115, scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'actions', width: 75, fixed: 'right', scopedSlots: { customRender: 'actions' } }
      ]
    }
  },
  created () { this.load() },
  methods: {
    emptyForm () { return { id: undefined, accountName: '', bankName: '', accountHolder: '', accountNo: '', operatingSubjectId: 0, remark: '' } },
    async load () {
      this.loading = true
      try { const res = await listFinanceCashAccounts({ keyword: this.keyword || undefined, operatingSubjectId: this.subjectId }); this.rows = res.data || [] } finally { this.loading = false }
    },
    reset () { this.keyword = ''; this.subjectId = undefined; this.load() },
    openCreate () { this.form = this.emptyForm(); this.visible = true },
    openEdit (row) { this.form = { id: row.id, accountName: row.accountName, bankName: row.bankName || '', accountHolder: row.accountHolder || '', accountNo: row.accountNo || '', operatingSubjectId: Number(row.operatingSubjectId || 0), remark: row.remark || '' }; this.visible = true },
    submit () {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        try { await saveFinanceCashAccount(this.form); this.$message.success('资金账户已保存'); this.visible = false; this.load() } finally { this.saving = false }
      })
    },
    changeStatus (row, enabled) {
      this.$confirm({ title: `${enabled ? '启用' : '停用'}资金账户“${row.accountName}”？`, content: enabled ? '启用后可用于新建收款单。' : '停用不影响历史收款单，但新单不再可选。', onOk: async () => { this.statusId = row.id; try { await updateFinanceCashAccountStatus({ id: row.id, enabled }); this.$message.success('状态已更新'); this.load() } finally { this.statusId = null } } })
    }
  }
}
</script>

<style scoped>
.account-manager{color:#26364b}.section-heading{margin-bottom:20px}.section-heading h3{margin:0 0 5px;font-size:18px}.section-heading p{margin:0;color:#718096}.filter-bar{display:grid;grid-template-columns:minmax(260px,1.3fr) minmax(180px,.7fr) auto;gap:16px;align-items:end;padding:16px;background:#f7f9fc;border:1px solid #e7ebf1;border-radius:8px}.filter-bar label{display:flex;flex-direction:column;gap:7px}.filter-bar label span{font-size:12px;color:#5f6f82;font-weight:600}.filter-actions{display:flex;gap:8px}.table-toolbar{display:flex;align-items:center;justify-content:space-between;margin:18px 0 12px}.table-toolbar span{font-size:12px;color:#8290a2}.status-text{margin-left:8px;color:#687789}.subject-pill{display:inline-block;padding:2px 8px;background:#edf4ff;color:#2768bd;border-radius:10px;font-size:12px}.mono{font-family:SFMono-Regular,Consolas,monospace}.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 18px}.form-grid .full{grid-column:1/-1}@media(max-width:760px){.filter-bar,.form-grid{grid-template-columns:1fr}.filter-actions{justify-content:flex-end}.form-grid .full{grid-column:auto}.table-toolbar{align-items:flex-start;gap:10px;flex-direction:column}}
</style>
